import { useEffect, useState } from 'react';
import { useStore } from '../store.ts';
import { useT } from '../i18n.ts';
import { Sidebar } from './Sidebar.tsx';
import { ThreadView } from './Thread.tsx';
import { NewChatDialog } from './NewChat.tsx';
import { AppDownloads, SettingsDialog } from './Settings.tsx';
import { Dialog } from './Dialog.tsx';
import { AdminDialog } from './Admin.tsx';
import { UpdateBanner } from './UpdateBanner.tsx';
import { FaceIdOffer } from './FaceIdOffer.tsx';
import { AppLock } from './AppLock.tsx';

export type Panel = null | 'new' | 'settings' | 'admin' | 'apps';

export function Shell() {
  const activeId = useStore((s) => s.activeId);
  const conn = useStore((s) => s.conn);
  const provider = useStore((s) => s.provider);
  const t = useT();
  const isAdmin = useStore((s) => s.me?.isAdmin ?? false);
  // "/?admin=users" (from the push sent when someone forgot their password) opens Administration → People.
  const [panel, setPanel] = useState<Panel>(() => (isAdmin && new URLSearchParams(location.search).get('admin') === 'users' ? 'admin' : null));
  const [adminTab, setAdminTab] = useState<'users' | undefined>(() => (new URLSearchParams(location.search).get('admin') === 'users' ? 'users' : undefined));
  useEffect(() => {
    const open = () => {
      if (!isAdmin) return;
      setAdminTab('users');
      setPanel('admin');
    };
    window.addEventListener('tc-open-admin-users', open);
    return () => window.removeEventListener('tc-open-admin-users', open);
  }, [isAdmin]);

  return (
    <div className={`shell ${activeId ? 'has-active' : ''}`}>
      {conn !== 'live' && conn !== 'connecting' && (
        <div className={`conn-banner conn-${conn}`} role="status">
          {conn === 'polling' ? t('connPolling') : t('connOffline')}
        </div>
      )}
      {provider === 'mock' && <div className="mock-banner" role="note">{t('mockNotice')}</div>}
      <UpdateBanner />
      <FaceIdOffer />
      <AppLock />
      <div className="shell-body">
        <Sidebar onPanel={setPanel} />
        <ThreadView />
      </div>
      {panel === 'new' && <NewChatDialog onClose={() => setPanel(null)} />}
      {panel === 'settings' && <SettingsDialog onClose={() => setPanel(null)} />}
      {panel === 'apps' && <AppsDialog onClose={() => setPanel(null)} />}
      {panel === 'admin' && <AdminDialog initialTab={adminTab} onClose={() => (setPanel(null), setAdminTab(undefined))} />}
    </div>
  );
}

function AppsDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  return (
    <Dialog title={t('downloadApp')} onClose={onClose}>
      <AppDownloads showEmpty />
    </Dialog>
  );
}
